import useIsFacetActive from 'client/hooks/useIsFacetActive';
import { useDrawerState } from 'client/state/Drawer';
import { useFacetListState } from 'client/state/FacetList';
import { FILTER_DRAWER_BUTTON_ID } from 'client/utils/consts';
import { PLPGeneralFacetValue, PLPGroupedFacetValue } from 'common/types/plp-data';
import React, { FormEventHandler, useCallback, useRef } from 'react';
import DrawerInner from '../../Drawer/DrawerInner/DrawerInner';
import { AccordionButton } from './AccordionButton';
import { FacetItemInternals } from './FacetemInternals';

export type DrawerFacetValuesProps = {
  id: string;
  name: string;
  values: PLPGroupedFacetValue[] | PLPGeneralFacetValue[];
  onChange: FormEventHandler<HTMLElement>;
  onKeyDown: FormEventHandler<HTMLElement>;
  isBaseFacetType: boolean;
};

function DrawerFacetValues({ id, name, isBaseFacetType, values, onChange, onKeyDown }: DrawerFacetValuesProps) {
  const setActiveFacet = useFacetListState((s) => s.setActiveFacet);
  const toggleDrawerOpen = useDrawerState((s) => s.toggleDrawerOpen);
  const drawerOpen = useDrawerState((s) => s.drawerOpen);
  const facetButtonRef = useRef<HTMLDivElement>(null);

  const isFacetActive = useIsFacetActive(id);

  const onDrawerBack = useCallback(() => {
    setActiveFacet();
    toggleDrawerOpen.on();
    facetButtonRef.current?.focus();
  }, [setActiveFacet, toggleDrawerOpen]);

  const onCloseHandler = useCallback(() => {
    setActiveFacet();
    toggleDrawerOpen.off();
    setTimeout(() => {
      const filterButton = document.getElementById(FILTER_DRAWER_BUTTON_ID);
      filterButton?.focus();
    }, 100);
  }, [setActiveFacet, toggleDrawerOpen]);

  const handleClick = useCallback(() => setActiveFacet(id), [id, setActiveFacet]);
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') setActiveFacet(id);
    },
    [id, setActiveFacet],
  );

  return (
    <>
      <AccordionButton
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        ref={facetButtonRef}
        data-testid="fuse-accordion__button"
      >
        {name}
      </AccordionButton>
      {drawerOpen && isFacetActive && (
        <DrawerInner name={name} open onCloseHandler={onCloseHandler} onDrawerBack={onDrawerBack}>
          <FacetItemInternals
            id={id}
            values={values}
            name={name}
            onChange={onChange}
            onKeyDown={onKeyDown}
            isBaseFacetType={isBaseFacetType}
          />
        </DrawerInner>
      )}
    </>
  );
}

export default React.memo(DrawerFacetValues);
